import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { GLASS_PRESETS, glassKey, lensPixels } from "./glass";
import "styles/ios-glass.css";

export const EFFECTS_KEY = "magellan-effects";
const readEffects = () => {
  try { return localStorage.getItem(EFFECTS_KEY) === "off" ? "off" : "on"; }
  catch { return "on"; }
};
const media = (q) => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(q).matches : false);
// Chromium is the only engine that renders SVG filters inside backdrop-filter (@supports cannot tell).
const chromium = () => Boolean(typeof navigator !== "undefined" && navigator.userAgentData?.brands?.some((b) => /Chromium/i.test(b.brand)));
// Safari (all iOS browsers) and Chrome on macOS already rubber-band natively.
const nativeElastic = () => typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "");

// Liquid-glass lens (Chromium only): every [data-glass] element inside an iOS shell gets an SVG
// filter built for its exact size, referenced through --glass-lens. Safari and Firefox drop the
// whole backdrop-filter when it contains url(), so they keep the plain blur and the CSS rim.
const SVG_NS = "http://www.w3.org/2000/svg";
const MAX_LENSED = 10;
function pngUrl(W, H, data) {
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  c.getContext("2d").putImageData(new ImageData(data, W, H), 0, 0);
  return c.toDataURL("image/png");
}
function filterMarkup(W, H, p, map, spec, scale) {
  const img = (href, result) => `<feImage href="${href}" x="0" y="0" width="${W}" height="${H}" preserveAspectRatio="none" result="${result}"/>`;
  return `<feFlood flood-color="rgb(128,128,128)" result="neutral"/>${img(map, "raw")}` +
    `<feComposite in="raw" in2="neutral" operator="over" result="map"/>` +
    `<feDisplacementMap in="SourceGraphic" in2="map" scale="${scale.toFixed(1)}" xChannelSelector="R" yChannelSelector="G" result="bent"/>` +
    `<feColorMatrix in="bent" type="saturate" values="${p.saturate}" result="body"/>${img(spec, "spec")}` +
    `<feColorMatrix in="bent" type="saturate" values="${p.rim}" result="vivid"/>` +
    `<feComposite in="vivid" in2="spec" operator="in" result="rimTint"/>` +
    `<feComponentTransfer in="spec" result="specSoft"><feFuncA type="linear" slope="${p.specular}"/></feComponentTransfer>` +
    `<feBlend in="rimTint" in2="body" mode="normal" result="lit"/><feBlend in="specSoft" in2="lit" mode="normal"/>`;
}
function installGlass() {
  const defs = document.createElementNS(SVG_NS, "svg");
  defs.setAttribute("aria-hidden", "true");
  defs.setAttribute("focusable", "false");
  defs.style.cssText = "position:absolute;width:0;height:0;overflow:hidden;pointer-events:none";
  document.body.appendChild(defs);
  const filters = new Map(), items = new Map();
  const drop = (key) => {
    const f = filters.get(key);
    if (f && --f.users <= 0) { f.node.remove(); filters.delete(key); }
  };
  const clear = (el) => {
    el.removeAttribute("data-lensed");
    el.style.removeProperty("--glass-lens");
  };
  const apply = (el) => {
    const item = items.get(el);
    if (!item || !el.isConnected) return;
    const name = GLASS_PRESETS[el.dataset.glass] ? el.dataset.glass : "capsule", p = GLASS_PRESETS[name];
    const W = Math.round(el.offsetWidth), H = Math.round(el.offsetHeight);
    const R = Math.min(parseFloat(getComputedStyle(el).borderTopLeftRadius) || 0, W / 2, H / 2);
    const key = W >= 16 && H >= 16 && W * H <= 2400000 ? glassKey(W, H, R, name) : null;
    if (key === item.key) return;
    if (item.key) drop(item.key);
    item.key = key;
    if (!key) { clear(el); return; }
    if (!filters.has(key)) {
      const { map, spec, maxShift } = lensPixels(W, H, R, p);
      const node = document.createElementNS(SVG_NS, "filter");
      [["id", key], ["x", "0"], ["y", "0"], ["width", "100%"], ["height", "100%"], ["color-interpolation-filters", "sRGB"]]
        .forEach(([k, v]) => node.setAttribute(k, v));
      node.innerHTML = filterMarkup(W, H, p, pngUrl(W, H, map), pngUrl(W, H, spec), 2 * maxShift * p.strength);
      defs.appendChild(node);
      filters.set(key, { node, users: 0 });
    }
    filters.get(key).users += 1;
    el.style.setProperty("--glass-lens", `url(#${key})`);
    el.setAttribute("data-lensed", "");
  };
  const release = (el) => {
    const item = items.get(el);
    if (!item) return;
    clearTimeout(item.timer);
    if (item.key) drop(item.key);
    clear(el);
    ro.unobserve(el);
    items.delete(el);
  };
  // Rebuild only once a size has settled (never per frame during a squeeze or slide).
  const ro = new ResizeObserver((entries) => entries.forEach(({ target }) => {
    const item = items.get(target);
    if (!item) return;
    clearTimeout(item.timer);
    item.timer = setTimeout(() => apply(target), 140);
  }));
  let frame = 0;
  const scan = () => {
    frame = 0;
    const found = [...document.querySelectorAll(".ios-app [data-glass]")].slice(0, MAX_LENSED);
    items.forEach((_, el) => { if (!found.includes(el)) release(el); });
    found.forEach((el) => {
      if (items.has(el)) return;
      items.set(el, { key: null, timer: 0 });
      ro.observe(el);
    });
  };
  const mo = new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(scan); });
  mo.observe(document.body, { childList: true, subtree: true });
  scan();
  return () => {
    cancelAnimationFrame(frame);
    mo.disconnect();
    [...items.keys()].forEach(release);
    ro.disconnect();
    defs.remove();
  };
}

let sheenInstalled = false;
function installSheen() {
  if (sheenInstalled || typeof document === "undefined") return;
  sheenInstalled = true;
  let frame = 0, last = null;
  document.addEventListener("pointermove", (e) => {
    last = e;
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      const el = last.target?.closest?.(".ios-sheen");
      if (!el) return;
      const r = el.getBoundingClientRect();
      el.style.setProperty("--mx", `${last.clientX - r.left}px`);
      el.style.setProperty("--my", `${last.clientY - r.top}px`);
    });
  }, { passive: true });
}

// Applies the effects preference while an iOS shell is mounted.
export function useAppEffects() {
  const [effects, setEffectsState] = useState(readEffects);
  useEffect(() => {
    const html = document.documentElement;
    const lowPower = media("(prefers-reduced-transparency: reduce)") || media("(prefers-contrast: more)");
    const lens = effects === "on" && !lowPower && chromium() && typeof ResizeObserver !== "undefined";
    html.dataset.effects = effects;
    html.dataset.lens = lens ? "on" : "off";
    if (effects === "on") installSheen();
    const uninstall = lens ? installGlass() : null;
    return () => { uninstall?.(); delete html.dataset.effects; delete html.dataset.lens; };
  }, [effects]);
  const setEffects = useCallback((value) => {
    const next = value === "off" ? "off" : "on";
    setEffectsState(next);
    try { localStorage.setItem(EFFECTS_KEY, next); } catch { /* Applies for this visit. */ }
  }, []);
  return { effects, setEffects };
}

// Elastic overscroll for browsers without it (Windows/Linux/Android Chrome, Firefox).
// target: element that moves; scroller: element or window that scrolls.
export function useRubberBand(targetRef, scrollerRef) {
  useEffect(() => {
    const target = targetRef.current;
    const scroller = scrollerRef ? scrollerRef.current : window;
    if (!target || !scroller || nativeElastic() || media("(prefers-reduced-motion: reduce)")) return undefined;
    const doc = () => document.scrollingElement || document.documentElement;
    const top = () => (scroller === window ? window.scrollY <= 0 : scroller.scrollTop <= 0);
    const bottom = () => (scroller === window
      ? window.innerHeight + window.scrollY >= doc().scrollHeight - 1
      : scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1);
    const resist = (d) => Math.sign(d) * Math.min(96, Math.sqrt(Math.abs(d)) * 6);
    let pull = 0, timer = 0, startY = null;
    const apply = (v, spring) => {
      target.style.transition = spring ? "transform 0.6s var(--ios-spring)" : "none";
      target.style.transform = v ? `translate3d(0, ${v}px, 0)` : "";
    };
    const release = () => { pull = 0; apply(0, true); };
    const onWheel = (e) => {
      if (e.ctrlKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      if (!((e.deltaY < 0 && top()) || (e.deltaY > 0 && bottom()))) return;
      pull -= e.deltaY;
      apply(resist(pull), false);
      clearTimeout(timer);
      timer = setTimeout(release, 110);
    };
    const onStart = (e) => { startY = e.touches[0].clientY; pull = 0; };
    const onMove = (e) => {
      if (startY == null) return;
      const d = e.touches[0].clientY - startY;
      if ((d > 0 && top()) || (d < 0 && bottom())) { pull = d; apply(resist(pull), false); }
    };
    const onEnd = () => { startY = null; if (pull) release(); };
    scroller.addEventListener("wheel", onWheel, { passive: true });
    scroller.addEventListener("touchstart", onStart, { passive: true });
    scroller.addEventListener("touchmove", onMove, { passive: true });
    scroller.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      clearTimeout(timer);
      scroller.removeEventListener("wheel", onWheel);
      scroller.removeEventListener("touchstart", onStart);
      scroller.removeEventListener("touchmove", onMove);
      scroller.removeEventListener("touchend", onEnd);
      target.style.transform = "";
      target.style.transition = "";
    };
  }, [targetRef, scrollerRef]);
}

// True once the element has scrolled under the sticky toolbar.
export function useScrolledPast(ref, offset = 52) {
  const [past, setPast] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([entry]) => setPast(!entry.isIntersecting), { rootMargin: `-${offset}px 0px 0px 0px` });
    io.observe(el);
    return () => io.disconnect();
  }, [ref, offset]);
  return past;
}

// Gel stretch when a thumb slides (iOS 26 glass flexes as it moves); skipped for reduced motion.
function useGelThumb(thumbRef, x) {
  const last = useRef(null);
  useEffect(() => {
    const el = thumbRef.current, from = last.current;
    last.current = x;
    if (!el || from == null || x == null || from === x || !el.animate) return;
    if (document.documentElement.dataset.effects !== "on" || media("(prefers-reduced-motion: reduce)")) return;
    el.style.transformOrigin = x > from ? "right center" : "left center";
    el.animate([{ scale: "1 1" }, { scale: "1.14 0.9", offset: 0.35 }, { scale: "1 1" }], { duration: 520, easing: "cubic-bezier(.3,.7,.3,1)" });
  }, [thumbRef, x]);
}

// iOS segmented control with a spring-animated thumb. While busy the buttons stay focusable
// (aria-disabled) so keyboard focus is not lost, and presses are ignored.
export function Segmented({ label, items, value, onChange, className = "", busy = false, glass = "capsule" }) {
  const wrap = useRef(null), knob = useRef(null), [thumb, setThumb] = useState(null);
  useGelThumb(knob, thumb?.x);
  useLayoutEffect(() => {
    const el = wrap.current?.querySelector('[aria-pressed="true"]');
    setThumb(el ? { x: el.offsetLeft, w: el.offsetWidth } : null);
  }, [value, items]);
  return (
    <div ref={wrap} className={`ios-segmented ${className}`} role="group" aria-label={label} data-glass={glass || undefined}>
      {thumb && <span ref={knob} className="ios-segmented-thumb" style={{ width: thumb.w, transform: `translateX(${thumb.x}px)` }} />}
      {items.map((item) => (
        <button key={item.value} type="button" aria-pressed={item.value === value} aria-disabled={busy || undefined}
          onClick={() => { if (!busy) onChange(item.value); }}>
          {item.label}
          {item.count ? <span className="ios-count">{item.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

// Route tabs: the iOS segmented control for sub-pages (links, not buttons), with a spring thumb.
// Scrolls sideways when there are many tabs; the active tab is kept in view.
export function RouteTabs({ label, items, className = "" }) {
  const wrap = useRef(null), knob = useRef(null), [thumb, setThumb] = useState(null);
  useGelThumb(knob, thumb?.x);
  const active = items.find((i) => i.active);
  useLayoutEffect(() => {
    const measure = () => {
      const el = wrap.current?.querySelector('[aria-current="page"]');
      setThumb(el ? { x: el.offsetLeft, w: el.offsetWidth } : null);
      return el;
    };
    const el = measure();
    if (el && el.scrollIntoView && wrap.current.scrollWidth > wrap.current.clientWidth) el.scrollIntoView({ block: "nearest", inline: "nearest" });
    // Counts arrive after the first paint; keep the thumb matched to the tab's real width.
    if (typeof ResizeObserver === "undefined" || !wrap.current) return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(wrap.current);
    wrap.current.querySelectorAll("a").forEach((a) => ro.observe(a));
    return () => ro.disconnect();
  }, [active?.to, items.length]);
  return (
    <nav className={`ios-tabs ${className}`} aria-label={label}>
      <div ref={wrap} className="ios-segmented ios-tabs-track" data-glass="capsule">
        {thumb && <span ref={knob} className="ios-segmented-thumb" style={{ width: thumb.w, transform: `translateX(${thumb.x}px)` }} />}
        {items.map((item) => (
          <Link key={item.to} to={item.to} aria-current={item.active ? "page" : undefined} className="ios-press">
            {item.label}
            {item.badge || null}
          </Link>
        ))}
      </div>
    </nav>
  );
}
